import { useEffect, useState, useRef } from "react";
import Skeleton from "react-loading-skeleton";
import { useAppSelector } from "hooks/reduxHooks";
import { selectCurrentExam } from "store/ExamStore/selectors";
import ReactPlayer from "react-player";
import { createExamVideoPresignedUrl } from "repositories/coachApi";
import { selectRequests } from "../../../store/LoadingStore/selectors";
import { Requests } from "../../../types/requests";
import { Button } from "components/Buttons/Button";
import { ButtonColorScheme, ButtonType } from "types/ui";
import { getData } from "../../../contexts/authContext";
import {
  Grid,
  CircularProgress,
  Slider,
  Box,
  Typography,
  Modal,
  LinearProgress,
} from "@mui/material";
import { icons } from "constants/icons";
import { useSelector } from "react-redux";
import { selectCurrentExamMLResult } from "../../../store/ExamMLResultStore/selectors";
//import testVideo from "../../../assets/video-test.mp4";
import { FFmpeg } from "@ffmpeg/ffmpeg";
// FFmpeg instance - create once and reuse
const ffmpeg = new FFmpeg();
// Load ffmpeg.wasm only once
let ffmpegLoaded = false;

export const VideoComponent = () => {
  const exam = useAppSelector(selectCurrentExam);
  const examMLResult = useSelector(selectCurrentExamMLResult);
  const [internalLoading, setInternalLoading] = useState<boolean>(true);
  const [skeletalLayer, setSkeletalLayer] = useState<boolean>(false);
  const [anglesCalculationsLayer, setAnglesCalculationsLayer] =
    useState<boolean>(false);

  // Timeline selection states
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const [timelineSelection, setTimelineSelection] = useState<[number, number]>([
    0, 20,
  ]);
  const [isTimelineSelectionEnabled, setIsTimelineSelectionEnabled] =
    useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState<number>(0);

  // Upload progress states
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadComplete, setUploadComplete] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const loading = useAppSelector((state: any) =>
    selectRequests([Requests.getExam], state)
  );
  const [originalLayerVideoRef, setOriginalLayer] =
    useState<ReactPlayer | null>();
  const [skeletalLayerVideoRef, setSkeletalLayerVideoRef] =
    useState<ReactPlayer | null>();
  const [anglesCalculationsLayerVideoRef, setAnglesCalculationsLayerVideoRef] =
    useState<ReactPlayer | null>();
  const [allLayersVideoRef, setAllLayersVideoRef] =
    useState<ReactPlayer | null>();
  const playerRef = useRef<ReactPlayer | null>(null);

  const [originalVideo, setOriginalVideo] = useState<any | null>();
  const [skeletalVideo, setSkeletalVideo] = useState<any | null>();
  const [anglesCalculationsVideo, setAnglesCalculationsVideo] = useState<
    any | null
  >();
  const [allLayersVideo, setAllLayersVideo] = useState<any | null>();

  const handleDownloadVideo = async () => {
    try {
      if (originalVideo) {
        const videoUrl = originalVideo;
        const videoRequest = new Request(videoUrl);
        fetch(videoUrl)
          .then((response) => {
            if (!response.ok) {
              throw new Error("Network response was not ok");
            }
            return response.blob();
          })
          .then((blob) => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "athleteDetail.mp4");
            document.body.appendChild(link);

            link.click();

            // Ensure link is removed after a short delay to allow the download to start
            setTimeout(() => {
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url); // Clean up the object URL
            }, 100);
          })
          .catch((error) => {
            console.error(
              "There has been a problem with your fetch operation:",
              error
            );
          });
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Function to download the selected segment of the video
  const handleDownloadSelectedSegment = async () => {
    try {
      if (originalVideo && timelineSelection[0] !== timelineSelection[1]) {
        // Show loading state

        setInternalLoading(true);

        // Make sure FFmpeg is loaded
        if (!ffmpegLoaded) {
          await ffmpeg.load();
          ffmpegLoaded = true;
        }

        // Get the input video as blob
        const response = await fetch(originalVideo);
        const inputBlob = await response.blob();

        // Convert blob to arrayBuffer
        const arrayBuffer = await inputBlob.arrayBuffer();
        const inputData = new Uint8Array(arrayBuffer);

        // Write input file to memory
        await ffmpeg.writeFile("input.mp4", inputData);

        // Define start and duration in seconds
        const startTime = timelineSelection[0];
        const duration = timelineSelection[1] - timelineSelection[0];

        // Execute FFmpeg command to crop video
        await ffmpeg.exec([
          "-i",
          "input.mp4",
          "-ss",
          `${startTime}`,
          "-t",
          `${duration}`,
          "-c:v",
          "copy",
          "-c:a",
          "copy",
          "output.mp4",
        ]);

        // Read the output file
        const outputData = await ffmpeg.readFile("output.mp4");

        // Create a blob from the output data
        const blob = new Blob([outputData], { type: "video/mp4" });
        const url = URL.createObjectURL(blob);

        // Get presigned URL for upload
        const presignedUrl = await createExamVideoPresignedUrl({
          contentType: "video/mp4",
          patientId: exam.patientId,
          examId: exam._id,
          fileExtension: "mp4",
        });

        if (presignedUrl.uploadUrl) {
          const uploadUrl = presignedUrl.uploadUrl;

          // Reset upload states
          setUploadProgress(0);
          setIsUploading(true);
          setUploadComplete(false);
          setUploadError(null);

          // Use XMLHttpRequest for upload progress tracking
          const xhr = new XMLHttpRequest();

          // Set up event listeners
          xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) {
              const progress = Math.round((event.loaded / event.total) * 100);
              setUploadProgress(progress);
              console.log(`Upload Progress: ${progress}%`);
            }
          });

          xhr.addEventListener("load", async () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              setUploadComplete(true);
              setIsUploading(false);
              console.log("Upload completed successfully");

              // Here you could call a validation endpoint if needed
              // Similar to the validateVideo call in the provided example
              // await mlApi.validateVideo({ videoPath: uploadUrl });

              setTimeout(() => {
                setUploadComplete(false);
              }, 3000);
            } else {
              setUploadError(`Upload failed with status: ${xhr.status}`);
              setIsUploading(false);
              console.error("Upload failed:", xhr.statusText);
            }
          });

          xhr.addEventListener("error", () => {
            setUploadError("Upload failed due to network error");
            setIsUploading(false);
            console.error("Upload failed due to network error");
          });

          xhr.addEventListener("abort", () => {
            setUploadError("Upload was aborted");
            setIsUploading(false);
            console.log("Upload aborted");
          });

          // Open and send the request
          xhr.open("PUT", uploadUrl, true);
          xhr.setRequestHeader("Content-Type", "video/mp4");
          xhr.send(blob);
        }

        // Hide loading state
        setInternalLoading(false);
      } else {
        alert("Please select a valid segment of the video to crop");
      }
    } catch (error) {
      console.error("Error cropping video:", error);
      setInternalLoading(false);
      setUploadError("Error processing video");
      setIsUploading(false);
      alert("There was an error processing the video. Please try again.");
    }
  };

  const resetVideoPlayers = () => {
    setOriginalVideo(null);
    setOriginalLayer(null);
    setAnglesCalculationsVideo(null);
    setAnglesCalculationsLayerVideoRef(null);
    setSkeletalVideo(null);
    setSkeletalLayerVideoRef(null);
    setAllLayersVideo(null);
    setAllLayersVideoRef(null);
    setVideoDuration(0);
    setCurrentPlaybackTime(0);
  };

  useEffect(() => {
    if (
      originalLayerVideoRef &&
      skeletalLayerVideoRef &&
      anglesCalculationsLayerVideoRef
    ) {
      if (skeletalLayer && !anglesCalculationsLayer) {
        skeletalLayerVideoRef.seekTo(0, "seconds");
      }
      if (anglesCalculationsLayer && !skeletalLayer) {
        anglesCalculationsLayerVideoRef.seekTo(0, "seconds");
      }
      if (anglesCalculationsLayer && skeletalLayer) {
        // @ts-ignore: Unreachable code error
        allLayersVideoRef.seekTo(0, "seconds");
      }
      if (!anglesCalculationsLayer && !skeletalLayer) {
        originalLayerVideoRef.seekTo(0, "seconds");
      }
    }
  }, [skeletalLayer, allLayersVideoRef, anglesCalculationsLayer]);

  useEffect(() => {
    const fetchData = async () => {
      if (exam._id) {
        setInternalLoading(true);
        resetVideoPlayers();
        await Promise.all([
          getVideoFromUrl(
            `${process.env.REACT_APP_EXAM_VIDEO_S3_URL}/unprocessed/${exam.patientId}/${exam._id}.mp4`,
            setOriginalVideo
          ),
          getVideoFromUrl(
            `${process.env.REACT_APP_EXAM_VIDEO_S3_URL}/processed/${exam.patientId}/${exam._id}-angles.mp4`,
            setAnglesCalculationsVideo
          ),
          getVideoFromUrl(
            `${process.env.REACT_APP_EXAM_VIDEO_S3_URL}/processed/${exam.patientId}/${exam._id}-skeleton.mp4`,
            setSkeletalVideo
          ),
          getVideoFromUrl(
            `${process.env.REACT_APP_EXAM_VIDEO_S3_URL}/processed/${exam.patientId}/${exam._id}.mp4`,
            setAllLayersVideo
          ),
        ]);

        setTimeout(() => {
          setInternalLoading(false);
        }, 10000);
      }
    };
    fetchData();
  }, [exam]);

  async function getVideoFromUrl(url: string, setVideo: any) {
    try {
      const token = await getData("idToken");
      const options = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      fetch(url, options)
        .then((response) => response.blob())
        .then((blob) => {
          setVideo(URL.createObjectURL(blob));
        });
    } catch (err) {
      console.log(err);
    }
  }

  const getVideoUrl = () => {
    // For local testing, return the local video file
    /*     if (process.env.NODE_ENV === "development") {
      return testVideo;
    } */

    // Original logic for production
    if (!examMLResult.result) {
      return originalVideo;
    }

    if (!skeletalLayer && !anglesCalculationsLayer) {
      return originalVideo;
    }

    if (skeletalLayer && anglesCalculationsLayer) {
      return allLayersVideo;
    }

    if (skeletalLayer && !anglesCalculationsLayer) {
      return skeletalVideo;
    }

    if (!skeletalLayer && anglesCalculationsLayer) {
      return anglesCalculationsVideo;
    }
  };

  const setVideoRef = (rp: ReactPlayer | null) => {
    playerRef.current = rp;

    if (!skeletalLayer && !anglesCalculationsLayer) {
      setOriginalLayer(rp);
      return () => {};
    }

    if (skeletalLayer && anglesCalculationsLayer) {
      setAllLayersVideoRef(rp);
      return () => {};
    }

    if (skeletalLayer && !anglesCalculationsLayer) {
      setSkeletalLayerVideoRef(rp);
      return () => {};
    }

    if (!skeletalLayer && anglesCalculationsLayer) {
      setAnglesCalculationsLayerVideoRef(rp);
      return () => {};
    }
  };

  // Handlers for timeline selection
  const handleTimelineChange = (event: Event, newValue: number | number[]) => {
    // Check which value changed by comparing with current timelineSelection
    const newValueArray = newValue as [number, number];
    const startChanged = newValueArray[0] !== timelineSelection[0];
    const endChanged = newValueArray[1] !== timelineSelection[1];

    // Update the timeline selection state
    setTimelineSelection(newValueArray);

    // Only seek to the start position if the start value changed
    if (playerRef.current && startChanged) {
      playerRef.current.seekTo(newValueArray[0], "seconds");
    }
    if (playerRef.current && endChanged) {
      playerRef.current.seekTo(newValueArray[1], "seconds");
    }

    // Optionally log which value changed
    if (startChanged) {
      console.log("Start position changed:", newValueArray[0]);
    }
    if (endChanged) {
      console.log("End position changed:", newValueArray[1]);
    }
  };

  const handleVideoReady = () => {
    if (playerRef.current) {
      const duration = playerRef.current.getDuration();
      setVideoDuration(duration);
    }
  };

  const handlePlayback = () => {
    if (playerRef.current) {
      if (isTimelineSelectionEnabled) {
        // If we're at the end of selection, reset to beginning of selection
        if (currentPlaybackTime >= timelineSelection[1]) {
          playerRef.current.seekTo(timelineSelection[0], "seconds");
        }
        setIsPlaying(true);
      } else {
        setIsPlaying(true);
      }
    }
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleProgress = (state: { playedSeconds: number }) => {
    setCurrentPlaybackTime(state.playedSeconds);

    // If timeline selection is enabled, handle looping within the selection
    if (
      isTimelineSelectionEnabled &&
      state.playedSeconds >= timelineSelection[1]
    ) {
      playerRef.current?.seekTo(timelineSelection[0], "seconds");
      setIsPlaying(true);
    }
  };

  // Format seconds to mm:ss format
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  // Upload Progress Modal
  const uploadProgressModal = (
    <Modal
      open={isUploading || uploadComplete || uploadError !== null}
      onClose={() => {
        if (!isUploading) {
          setUploadComplete(false);
          setUploadError(null);
        }
        const now = new Date();
        const item = {
          value: true,
          examId: exam._id,
          expiry: now.getTime() + 3 * 60 * 1000, // 3 minutes
        };
        localStorage.setItem("trimmed-video", JSON.stringify(item));
        window.location.reload();
      }}
      aria-labelledby="upload-progress-modal"
      aria-describedby="modal-showing-upload-progress"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}
      >
        {isUploading && (
          <>
            <Typography variant="h6" component="h2" gutterBottom>
              Uploading Video
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Please wait while your video is being uploaded... {uploadProgress}
              %
            </Typography>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{ mb: 2 }}
            />
          </>
        )}

        {uploadComplete && (
          <>
            <Typography
              variant="h6"
              component="h2"
              gutterBottom
              sx={{ color: "green" }}
            >
              Upload Complete
            </Typography>
            <Typography variant="body2">
              Your video has been successfully uploaded.
            </Typography>
            <Typography variant="body2">
              Results may take a couple of minutes to be updated.
            </Typography>
          </>
        )}

        {uploadError && (
          <>
            <Typography
              variant="h6"
              component="h2"
              gutterBottom
              sx={{ color: "error.main" }}
            >
              Upload Failed
            </Typography>
            <Typography variant="body2">{uploadError}</Typography>
            <Button
              title="Close"
              onClick={() => setUploadError(null)}
              type={ButtonType.small}
              colorScheme={ButtonColorScheme.default}
              style={{ marginTop: 16 }}
            />
          </>
        )}
      </Box>
    </Modal>
  );

  return (
    <>
      {loading || internalLoading ? (
        <>
          <Skeleton width={268} height={580} />
        </>
      ) : (
        <div className="items-center">
          <div className="absolute top-5 left-7 z-50">
            <Button
              title="Skeletal"
              onClick={() => setSkeletalLayer(!skeletalLayer)}
              type={ButtonType.small}
              classNames="self-center"
              colorScheme={
                !skeletalLayer
                  ? ButtonColorScheme.default
                  : ButtonColorScheme.accent
              }
            />
            <Button
              title="Angle"
              onClick={() =>
                setAnglesCalculationsLayer(!anglesCalculationsLayer)
              }
              type={ButtonType.small}
              classNames="self-center ml-24"
              colorScheme={
                !anglesCalculationsLayer
                  ? ButtonColorScheme.default
                  : ButtonColorScheme.accent
              }
            />
          </div>

          {/* Upload Progress Modal */}
          {uploadProgressModal}

          {internalLoading ? (
            <div
              style={{
                height: "300px",
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <>
              <ReactPlayer
                url={getVideoUrl()}
                className="player"
                width="100%"
                height="auto"
                controls={true}
                playing={isPlaying}
                ref={setVideoRef}
                onReady={(reactVideo) => {
                  reactVideo.getInternalPlayer().style.borderRadius = "30px";
                  handleVideoReady();
                }}
                onProgress={handleProgress}
                onPlay={handlePlayback}
                onPause={handlePause}
                progressInterval={100}
                style={{
                  visibility: "visible",
                  borderRadius: "30px",
                }}
              />

              {/* Timeline selection controls */}
              <Box sx={{ width: "100%", mt: 3, px: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <Button
                      title={
                        isTimelineSelectionEnabled
                          ? "Disable Video Trimming"
                          : "Enable Video Trimming"
                      }
                      onClick={() =>
                        setIsTimelineSelectionEnabled(
                          !isTimelineSelectionEnabled
                        )
                      }
                      type={ButtonType.small}
                      colorScheme={
                        !isTimelineSelectionEnabled
                          ? ButtonColorScheme.default
                          : ButtonColorScheme.accent
                      }
                    />
                  </Grid>
                  {isTimelineSelectionEnabled && (
                    <>
                      <Grid item xs={12} sm={12}>
                        <Typography variant="body2">
                          Start: {formatTime(timelineSelection[0])}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <Typography variant="body2">
                          End: {formatTime(timelineSelection[1])}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <Typography variant="body2">
                          Duration:{" "}
                          {formatTime(
                            timelineSelection[1] - timelineSelection[0]
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ px: 1 }}>
                          <Slider
                            value={timelineSelection}
                            onChange={handleTimelineChange}
                            valueLabelDisplay="auto"
                            valueLabelFormat={formatTime}
                            min={0}
                            max={videoDuration}
                            step={0.1}
                            disableSwap
                            sx={{
                              "& .MuiSlider-thumb": {
                                color: "#4D7CFE",
                              },
                              "& .MuiSlider-track": {
                                color: "#4D7CFE",
                              },
                              "& .MuiSlider-rail": {
                                color: "#AEC6FF",
                              },
                            }}
                          />
                        </Box>
                      </Grid>
                      <Grid item>
                        <Button
                          title="Play Selection"
                          onClick={() => {
                            if (playerRef.current) {
                              playerRef.current.seekTo(
                                timelineSelection[0],
                                "seconds"
                              );
                              setIsPlaying(true);
                            }
                          }}
                          type={ButtonType.small}
                          colorScheme={ButtonColorScheme.accent}
                        />
                      </Grid>
                      <Grid item>
                        <Button
                          title="Trim Video"
                          onClick={handleDownloadSelectedSegment}
                          type={ButtonType.small}
                          colorScheme={ButtonColorScheme.default}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            </>
          )}

          <Grid container justifyContent="center" spacing={2} mt={5}>
            <Grid item>
              <Button
                title="Download"
                onClick={handleDownloadVideo}
                iconPath={icons.download}
                type={ButtonType.normal}
                style={{
                  fontSize: "18px",
                  fontWeight: "400",
                  backgroundColor: "#FFFFFF",
                  color: "#5C5D68",
                  borderRadius: 10,
                  boxShadow: "0 10px 10px rgba(0, 0, 0, 0.1)",
                }}
              />
            </Grid>
          </Grid>
        </div>
      )}
    </>
  );
};
